import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import {
  PROJECT_STATIONS,
  PRIORITIES,
  STORAGE_DETAILS,
  getLastUsedStation,
  setLastUsedStation,
} from "../lib/constants";
import QRScanner from "../components/QRScanner";
import AuthImg from "../components/AuthImg";
import CrewLeaderPicker from "../components/CrewLeaderPicker";
import SubstationCombobox from "../components/SubstationCombobox";
import EquipmentStockPreview from "../components/EquipmentStockPreview";
import { useAuth } from "../contexts/AuthContext";
import { useDropdown } from "../lib/useAppConfig";
import {
  ScanLine,
  Save,
  Upload,
  X,
  ChevronLeft,
  Inbox,
  ArrowRight,
  Zap,
  Layers,
  CheckCircle2,
  Plus,
  Camera,
  Loader2,
  Sparkles,
  CircleAlert,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Step 1 of the Yard Runner workflow: RECEIVE.
 * Dual-mode tabbed UI:
 *   - Quick Receive (default on mobile): 10-20s flow, scan + 4 fields + 1 photo, auto-defaults the rest.
 *   - Full Receive: detailed form (existing behavior).
 * After save, runner is taken to a success screen with "Receive Another" CTA, or routed to Step 2.
 */
export default function LogShipment() {
  const nav = useNavigate();
  const { isRunnerLevel } = useAuth();
  const [mode, setMode] = useState(() => {
    const saved = typeof window !== "undefined" && localStorage.getItem("rougee:receive_mode");
    if (saved === "quick" || saved === "full") return saved;
    return "quick"; // Quick is the default on every device per spec
  });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [success, setSuccess] = useState(null); // { mr, id }

  useEffect(() => {
    if (!isRunnerLevel) {
      toast.error("Only Yard Runner / Supervisor / Admin can log a new shipment");
      nav("/", { replace: true });
    }
  }, [isRunnerLevel, nav]);

  useEffect(() => {
    try {
      localStorage.setItem("rougee:receive_mode", mode);
    } catch (e) {
      // Storage quota / private-browsing — non-fatal; log for diagnostics.
      console.error("Persisting receive mode failed", e);
    }
  }, [mode]);

  const blankFull = {
    MR_Number: "",
    Project_Station: getLastUsedStation() || PROJECT_STATIONS[0],
    Assigned_Crew_Leader: "",
    Current_Location: "Main yard",
    Other_Location: "",
    Storage_Detail: "",
    Storage_Other: "",
    Status: "Received - In Main Yard",
    Workflow_Stage: "Received",
    Preparation_Queue_Status: "Pending Preparation",
    Priority: "Normal",
    Contents: "",
    Minor_Materials: "",
    Equipment_Needed: "",
    Unavailable_Items: "",
    Notes: "",
    Images: [],
  };
  const blankQuick = {
    MR_Number: "",
    Project_Station: getLastUsedStation() || "",
    Contents: "",
    Images: [],
  };
  const [form, setForm] = useState(blankFull);
  const [quick, setQuick] = useState(blankQuick);
  const [ocr, setOcr] = useState({ busy: false, suggestion: "", confidence: 0, raw: "", source: null }); // OCR state for Quick mode
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setQuickField = (k, v) => setQuick((f) => ({ ...f, [k]: v }));

  const onScanQuick = (text) => { setScannerOpen(false); setQuickField("MR_Number", text.toUpperCase()); toast.success(`Scanned MR ${text}`); };
  const onScanFull  = (text) => { setScannerOpen(false); setField("MR_Number", text.toUpperCase()); toast.success(`Scanned MR ${text}`); };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/files/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.path;
  };

  const onUploadFull = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setImageUploading(true);
    try {
      for (const f of files) {
        const path = await uploadOne(f);
        setForm((curr) => ({ ...curr, Images: [...curr.Images, path] }));
      }
      toast.success(`Uploaded ${files.length} image${files.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const onUploadQuick = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setImageUploading(true);
    setOcr({ busy: true, suggestion: "", confidence: 0, raw: "", source: null });
    try {
      // Run upload + OCR in parallel — both consume the file
      // (We must read the file twice → use FormData with cloned blobs)
      const fdUpload = new FormData();
      fdUpload.append("file", file);
      const fdOcr = new FormData();
      fdOcr.append("file", file);
      const [uploadRes, ocrRes] = await Promise.allSettled([
        api.post("/files/upload", fdUpload, { headers: { "Content-Type": "multipart/form-data" } }),
        api.post("/ocr/mr-number", fdOcr, { headers: { "Content-Type": "multipart/form-data" } }),
      ]);
      if (uploadRes.status === "fulfilled") {
        const path = uploadRes.value.data.path;
        setQuick((curr) => ({ ...curr, Images: [path] }));
      } else {
        toast.error(formatApiError(uploadRes.reason?.response?.data?.detail) || "Upload failed");
      }
      if (ocrRes.status === "fulfilled") {
        const { mr_number, raw, found, confidence } = ocrRes.value.data;
        const conf = Number.isFinite(confidence) ? confidence : 0;
        if (found && mr_number) {
          setOcr({ busy: false, suggestion: mr_number, confidence: conf, raw, source: "ai" });
          // If field is empty, prefill it; otherwise leave user's typing alone but show suggestion banner
          setQuick((curr) => curr.MR_Number ? curr : { ...curr, MR_Number: mr_number });
          toast.success(`AI read MR: ${mr_number} · ${conf}% confidence`);
        } else {
          setOcr({ busy: false, suggestion: "", confidence: 0, raw, source: "ai_none" });
          toast.message("No MR detected — please type it manually", { duration: 3000 });
        }
      } else {
        setOcr({ busy: false, suggestion: "", confidence: 0, raw: "", source: "ai_error" });
        // Don't toast — upload may still have succeeded
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
      setOcr({ busy: false, suggestion: "", confidence: 0, raw: "", source: "ai_error" });
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const removeImageFull = (path) => setForm((f) => ({ ...f, Images: f.Images.filter((p) => p !== path) }));
  const removeImageQuick = () => {
    setQuick((f) => ({ ...f, Images: [] }));
    setOcr({ busy: false, suggestion: "", confidence: 0, raw: "", source: null });
  };

  const submitFull = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.MR_Number.trim()) return setError("MR Number is required.");
    if (!form.Project_Station.trim()) return setError("Project / Station is required.");
    if (form.Current_Location === "Main yard" && !(form.Storage_Detail || "").trim()) {
      return setError("Stocking Location is required when Current Location is Main yard.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/shipments", form);
      setLastUsedStation(form.Project_Station);
      toast.success(`Received MR ${data.MR_Number} — proceed to stock`);
      nav(`/shipments/${data.id}/stock`, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuick = async (e) => {
    e.preventDefault();
    setError("");
    if (!quick.MR_Number.trim()) return setError("MR Number is required.");
    if (!(quick.Storage_Detail || "").trim()) {
      return setError("Stocking Location is required (where it will be stored in Main yard).");
    }
    setSubmitting(true);
    try {
      const payload = {
        MR_Number: quick.MR_Number.trim(),
        Project_Station: (quick.Project_Station || "").trim() || PROJECT_STATIONS[0],
        Current_Location: "Main yard",
        Other_Location: "",
        Storage_Detail: quick.Storage_Detail,
        Storage_Other: "",
        Status: "Received - In Main Yard",
        Workflow_Stage: "Received",
        Preparation_Queue_Status: "Pending Preparation",
        Priority: "Normal",
        Contents: (quick.Contents || "").trim(),
        Minor_Materials: "",
        Equipment_Needed: "",
        Unavailable_Items: "",
        Assigned_Crew_Leader: "",
        Notes: "",
        Images: quick.Images || [],
      };
      const { data } = await api.post("/shipments", payload);
      if (payload.Project_Station) setLastUsedStation(payload.Project_Station);
      toast.success(`Quick Receive done — MR ${data.MR_Number}`);
      setSuccess({ mr: data.MR_Number, id: data.id });
      setQuick({ ...blankQuick, Project_Station: payload.Project_Station });
      setOcr({ busy: false, suggestion: "", confidence: 0, raw: "", source: null });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen — shown right after a Quick Receive succeeds
  if (success) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-32 md:pb-10" data-testid="quick-receive-success">
        <div className="rg-card p-8 text-center border-l-[6px] border-l-green-700">
          <div className="h-20 w-20 mx-auto bg-green-700 text-white grid place-items-center rounded-full mb-4">
            <CheckCircle2 strokeWidth={2.5} className="h-12 w-12" />
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-green-700">
            Success
          </div>
          <h2 className="font-display text-3xl uppercase text-slate-900 mt-1 mb-2">
            Added to Pending Queue
          </h2>
          <div className="font-mono text-2xl text-slate-900 mb-1">
            MR <span className="font-bold" data-testid="quick-receive-success-mr">{success.mr}</span>
          </div>
          <p className="text-sm text-slate-600 mb-6">
            The Yard Runner will pick it up next. You can keep going.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setSuccess(null)}
              data-testid="quick-receive-another-btn"
              className="rg-tap-xl bg-green-700 hover:bg-green-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 border-2 border-green-900 px-6 min-h-[64px] text-base"
            >
              <Plus strokeWidth={3} className="h-5 w-5" />
              Receive Another
            </button>
            <Link
              to={`/shipments/${success.id}`}
              data-testid="quick-receive-view-btn"
              className="rg-tap-xl bg-white border-2 border-slate-300 hover:border-slate-900 text-slate-900 font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 px-6 min-h-[64px] text-base"
            >
              View details
              <ArrowRight strokeWidth={3} className="h-5 w-5" />
            </Link>
            <Link
              to="/preparation-queue"
              data-testid="quick-receive-queue-btn"
              className="rg-tap-xl bg-white border-2 border-slate-300 hover:border-slate-900 text-slate-900 font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 px-6 min-h-[64px] text-base"
            >
              See queue
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 md:pb-10">
      <button
        onClick={() => nav(-1)}
        data-testid="back-btn"
        className="flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-slate-600 hover:text-slate-900 mb-3"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      {/* Workflow step header */}
      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-green-700">
        <Inbox className="h-4 w-4" />
        Yard Runner — Step 1 of 4
      </div>
      <h1 className="font-display text-3xl uppercase text-slate-900 mb-1" data-testid="receive-page-title">
        Receive Shipments
      </h1>
      <p className="text-sm text-slate-600 mb-4">
        Scan or type the MR. <strong>Quick</strong> mode is the default — under 20 seconds per box.
      </p>

      {/* Very large scanner button — primary action at top of screen (per spec) */}
      <button
        type="button"
        onClick={() => setScannerOpen(true)}
        data-testid="receive-scanner-btn"
        className="rg-tap-xl w-full bg-slate-900 hover:bg-slate-800 text-white border-2 border-slate-900 rounded-sm flex items-center justify-center gap-3 font-display uppercase tracking-wider min-h-[96px] text-xl mb-4"
      >
        <ScanLine strokeWidth={3} className="h-9 w-9" />
        Scan barcode / QR
      </button>

      {/* Tabs */}
      <div
        className="grid grid-cols-2 gap-1 p-1 bg-slate-100 border-2 border-slate-200 rounded-sm mb-5"
        data-testid="receive-mode-tabs"
      >
        <button
          onClick={() => setMode("quick")}
          data-testid="receive-tab-quick"
          className={`rg-tap-xl rounded-sm font-display uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-colors ${
            mode === "quick"
              ? "bg-green-700 text-white border-2 border-green-900"
              : "bg-transparent text-slate-700 hover:bg-white border-2 border-transparent"
          }`}
        >
          <Zap strokeWidth={3} className="h-5 w-5" />
          Quick Receive
        </button>
        <button
          onClick={() => setMode("full")}
          data-testid="receive-tab-full"
          className={`rg-tap-xl rounded-sm font-display uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-colors ${
            mode === "full"
              ? "bg-slate-900 text-white border-2 border-slate-900"
              : "bg-transparent text-slate-700 hover:bg-white border-2 border-transparent"
          }`}
        >
          <Layers strokeWidth={3} className="h-5 w-5" />
          Full Receive
        </button>
      </div>

      {mode === "quick" ? (
        <QuickReceiveForm
          quick={quick}
          setQuickField={setQuickField}
          submitting={submitting}
          imageUploading={imageUploading}
          error={error}
          ocr={ocr}
          onUpload={onUploadQuick}
          removeImage={removeImageQuick}
          onSubmit={submitQuick}
        />
      ) : (
        <FullReceiveForm
          form={form}
          setField={setField}
          submitting={submitting}
          imageUploading={imageUploading}
          error={error}
          openScanner={() => setScannerOpen(true)}
          onUpload={onUploadFull}
          removeImage={removeImageFull}
          onSubmit={submitFull}
        />
      )}

      {scannerOpen && (
        <QRScanner
          onResult={mode === "quick" ? onScanQuick : onScanFull}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </div>
  );
}

function QuickReceiveForm({ quick, setQuickField, submitting, imageUploading, error, ocr, onUpload, removeImage, onSubmit }) {
  const hasPhoto = (quick.Images || []).length > 0;
  const aiBusy = ocr?.busy;
  const aiSuggestion = ocr?.suggestion;
  const aiConfidence = ocr?.confidence || 0;
  const aiNone = ocr?.source === "ai_none";
  const aiError = ocr?.source === "ai_error";
  const useSuggestion = () => setQuickField("MR_Number", aiSuggestion);

  // Refs for focus management — MR auto-focuses on mount; tapping the inline
  // "Submit" button focuses Stocking Location if missing, otherwise hits save.
  const mrRef = useRef(null);
  const storageRef = useRef(null);
  const submitBtnRef = useRef(null);
  useEffect(() => {
    // Defer one frame so it works even when the form first mounts behind a tab.
    const t = setTimeout(() => mrRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  // Color-code the confidence pill: green ≥85, amber 60-84, red <60
  const confTier =
    aiConfidence >= 85
      ? { bg: "bg-emerald-700", text: "text-white", label: "HIGH" }
      : aiConfidence >= 60
      ? { bg: "bg-amber-500", text: "text-slate-900", label: "MEDIUM" }
      : { bg: "bg-rose-700", text: "text-white", label: "LOW — DOUBLE-CHECK" };

  const mrFilled = !!quick.MR_Number.trim();
  const storageFilled = !!(quick.Storage_Detail || "").trim();
  const canSave = mrFilled && storageFilled && !submitting && !imageUploading;

  // Inline "Submit" next to the MR field. Smart behaviour:
  //   • If everything's valid → triggers the actual form submit (one-tap save).
  //   • If only Stocking Location is missing → scroll/focus the storage select
  //     so the runner sees exactly what's left.
  //   • If MR is empty → ignore (button is disabled in that state anyway).
  const onMrSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mrFilled) {
      mrRef.current?.focus();
      return;
    }
    if (!storageFilled) {
      storageRef.current?.focus();
      // Smooth-scroll the missing field into view on mobile.
      try { storageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
      return;
    }
    // Trigger the real form submit — re-uses the existing flow / error handling.
    submitBtnRef.current?.click();
  };

  // Pressing Enter inside the MR input also triggers the smart submit.
  const onMrKeyDown = (e) => {
    if (e.key === "Enter") onMrSubmit(e);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="quick-receive-form">
      <div className="rg-card p-3 bg-green-50 border-2 border-green-300 text-xs flex items-center gap-2">
        <Zap strokeWidth={3} className="h-4 w-4 text-green-700 flex-shrink-0" />
        <div className="text-green-900">
          <strong>Quick mode</strong> — type or scan the MR, pick a stocking spot, tap <em>Quick Receive</em>. Photo is optional.
        </div>
      </div>

      {/* ============ STEP 1 — MR Number (primary input) ============ */}
      <Field label="MR Number * (type or scan)" testid="field-quick-mr">
        <div className="flex gap-2">
          <input
            ref={mrRef}
            required
            value={quick.MR_Number}
            onChange={(e) => setQuickField("MR_Number", e.target.value.toUpperCase())}
            onKeyDown={onMrKeyDown}
            data-testid="quick-input-mr"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            className="rg-tap flex-1 min-w-0 border-2 border-slate-300 rounded-sm px-3 text-xl font-mono font-bold tracking-wider bg-white focus:border-green-700 outline-none min-h-[64px]"
            placeholder="MR-123456"
          />
          {/* Inline Submit — one-tap save when all required fields are set */}
          <button
            type="button"
            onClick={onMrSubmit}
            disabled={!mrFilled || submitting}
            data-testid="quick-mr-submit-btn"
            title={
              !mrFilled
                ? "Type the MR first"
                : !storageFilled
                ? "Pick a stocking spot to finish"
                : "Receive this shipment now"
            }
            className={`rg-tap flex-shrink-0 min-h-[64px] px-4 sm:px-5 rounded-sm font-display uppercase tracking-wider text-sm sm:text-base flex items-center gap-1.5 border-2 transition-colors ${
              canSave
                ? "bg-green-700 hover:bg-green-800 text-white border-green-900"
                : mrFilled
                ? "bg-amber-500 hover:bg-amber-600 text-slate-900 border-amber-700"
                : "bg-slate-200 text-slate-500 border-slate-300 cursor-not-allowed"
            }`}
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="hidden sm:inline">Receiving…</span>
              </>
            ) : canSave ? (
              <>
                <Save strokeWidth={3} className="h-5 w-5" />
                <span>Submit</span>
              </>
            ) : mrFilled ? (
              <>
                <ArrowRight strokeWidth={3} className="h-5 w-5" />
                <span>Next</span>
              </>
            ) : (
              <>
                <ArrowRight strokeWidth={3} className="h-5 w-5" />
                <span>Submit</span>
              </>
            )}
          </button>
        </div>
        {/* Persistent AI confidence pill — visible whenever an OCR result is in */}
        {hasPhoto && !aiBusy && aiSuggestion && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] flex-wrap" data-testid="ocr-confidence-row">
            <Sparkles strokeWidth={3} className="h-3 w-3 text-emerald-700" />
            <span className="text-slate-500">AI read</span>
            <span className="font-mono font-bold text-slate-900">{aiSuggestion}</span>
            <span
              data-testid="ocr-confidence-pill"
              className={`inline-flex items-center px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider border-2 ${confTier.bg} ${confTier.text} ${
                aiConfidence >= 85
                  ? "border-emerald-900"
                  : aiConfidence >= 60
                  ? "border-amber-700"
                  : "border-rose-900"
              }`}
              title="Model self-reported confidence"
            >
              {aiConfidence}% · {confTier.label}
            </span>
            {quick.MR_Number !== aiSuggestion && (
              <button
                type="button"
                onClick={useSuggestion}
                data-testid="ocr-use-suggestion-btn"
                className="ml-auto px-2 py-0.5 bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] font-bold uppercase tracking-wide rounded-sm border-2 border-emerald-900 flex items-center gap-1"
              >
                <CheckCircle2 strokeWidth={3} className="h-3 w-3" />
                Use AI
              </button>
            )}
          </div>
        )}
      </Field>

      {/* ============ STEP 2 — Stocking Location (required) ============ */}
      <Field
        label="Stocking Location * (Where it will be stored in Main yard)"
        testid="field-quick-storage"
      >
        <select
          ref={storageRef}
          required
          value={quick.Storage_Detail || ""}
          onChange={(e) => setQuickField("Storage_Detail", e.target.value)}
          data-testid="quick-input-storage"
          className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base font-bold bg-white focus:border-green-700 outline-none min-h-[56px]"
        >
          <option value="">— Pick a stocking spot —</option>
          {STORAGE_DETAILS.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </Field>

      {/* ============ STEP 3 — Optional details (collapsed by default look) ============ */}
      <div className="rg-card p-4 border-2 border-slate-200 bg-slate-50/50 space-y-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          Optional details
        </div>
        <Field label="Project / Station" testid="field-quick-station">
          <SubstationCombobox
            value={quick.Project_Station}
            onChange={(v) => setQuickField("Project_Station", v)}
            testid="quick-project-station"
            extraOptions={PROJECT_STATIONS}
          />
        </Field>
        <Field label="Contents (one short line)" testid="field-quick-contents">
          <input
            value={quick.Contents}
            onChange={(e) => setQuickField("Contents", e.target.value)}
            data-testid="quick-input-contents"
            className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-green-700 outline-none"
            placeholder="e.g. Pallet of transformers"
            maxLength={120}
          />
        </Field>
      </div>

      {/* ============ STEP 4 — Photo + AI OCR (optional, audit-only) ============ */}
      <div className="rg-card p-4 border-2 border-slate-200 bg-white space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
            <Camera className="h-3 w-3" />
            Photo & AI OCR (optional)
          </div>
          {hasPhoto && (
            <button
              type="button"
              onClick={() => removeImage()}
              data-testid="quick-retake-photo-btn"
              className="px-2 py-1 border-2 border-slate-300 hover:border-slate-900 rounded-sm bg-white text-[10px] font-bold uppercase tracking-wide text-slate-700 flex items-center gap-1"
              title="Retake photo"
            >
              <X strokeWidth={2.5} className="h-3 w-3" />
              Remove
            </button>
          )}
        </div>
        <label
          htmlFor="quick-image-upload-input"
          data-testid="quick-photo-mr-btn"
          className={`block cursor-pointer rounded-sm border-2 transition-colors ${
            hasPhoto
              ? "bg-emerald-50 border-emerald-400 text-emerald-900"
              : "bg-slate-50 hover:bg-slate-100 border-dashed border-slate-400 text-slate-700"
          }`}
        >
          <div className="flex items-center gap-3 p-3">
            {!hasPhoto ? (
              <div className="h-12 w-12 grid place-items-center bg-white text-slate-700 rounded-sm flex-shrink-0 border-2 border-slate-300">
                <Camera strokeWidth={2.5} className="h-6 w-6" />
              </div>
            ) : (
              <div className="h-12 w-12 overflow-hidden rounded-sm flex-shrink-0 border-2 border-emerald-500">
                <AuthImg path={quick.Images[0]} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm leading-tight">
                {hasPhoto
                  ? aiBusy
                    ? "AI reading the MR…"
                    : aiSuggestion
                    ? "Photo captured · AI read MR"
                    : "Photo captured"
                  : "Take a photo (optional)"}
              </div>
              <div className="text-[11px] opacity-80 mt-0.5">
                {hasPhoto
                  ? "Tap to retake. The AI suggestion appears under the MR field."
                  : "AI will try to read the handwritten MR — for audit / proof."}
              </div>
            </div>
            {aiBusy ? (
              <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />
            ) : (
              <Camera strokeWidth={2.5} className="h-5 w-5 opacity-70 flex-shrink-0" />
            )}
          </div>
        </label>
        <input
          id="quick-image-upload-input"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          onChange={onUpload}
          className="hidden"
          data-testid="quick-image-upload-input"
        />
        {/* AI suggestion banner — full callout when MR field is still empty */}
        {!aiBusy && aiSuggestion && quick.MR_Number !== aiSuggestion && (
          <div
            className="p-3 border-2 border-emerald-400 bg-emerald-50 rounded-sm flex items-start gap-2"
            data-testid="ocr-suggestion-banner"
          >
            <Sparkles strokeWidth={3} className="h-4 w-4 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">
                AI suggestion ({aiConfidence}% · {confTier.label})
              </div>
              <div className="font-mono font-bold text-lg text-slate-900 leading-none mt-0.5" data-testid="ocr-suggestion-mr">
                {aiSuggestion}
              </div>
              {aiConfidence < 60 && (
                <div className="text-[11px] text-rose-800 font-bold mt-1 flex items-center gap-1">
                  <CircleAlert className="h-3 w-3" />
                  Low confidence — double-check before saving.
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={useSuggestion}
              data-testid="ocr-use-suggestion-btn-full"
              className="px-2 py-1 bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] font-bold uppercase tracking-wide rounded-sm border-2 border-emerald-900 flex items-center gap-1 flex-shrink-0"
            >
              <CheckCircle2 strokeWidth={3} className="h-3 w-3" />
              Use
            </button>
          </div>
        )}
        {!aiBusy && hasPhoto && aiNone && (
          <div className="p-2 border-2 border-amber-300 bg-amber-50 text-amber-900 text-[11px] flex items-center gap-2 rounded-sm" data-testid="ocr-none-banner">
            <CircleAlert className="h-3.5 w-3.5 flex-shrink-0" />
            <span>AI couldn't find an MR on this photo — type it above.</span>
          </div>
        )}
        {!aiBusy && hasPhoto && aiError && (
          <div className="p-2 border-2 border-slate-300 bg-slate-50 text-slate-700 text-[11px] flex items-center gap-2 rounded-sm" data-testid="ocr-error-banner">
            <CircleAlert className="h-3.5 w-3.5 flex-shrink-0" />
            <span>AI service unavailable — type the MR above.</span>
          </div>
        )}
      </div>

      {error && (
        <div className="border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold px-3 py-2 rounded-sm" data-testid="quick-form-error">
          {error}
        </div>
      )}

      <div className="md:hidden h-24" />
      <div className="fixed md:static bottom-0 md:bottom-auto left-0 right-0 md:left-auto md:right-auto z-30 bg-white md:bg-transparent border-t-2 md:border-t-0 border-slate-300 p-4 md:p-0">
        <button
          ref={submitBtnRef}
          type="submit"
          disabled={!canSave}
          data-testid="quick-receive-submit"
          className="rg-tap-xl w-full bg-green-700 hover:bg-green-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60 min-h-[72px] text-lg sm:text-xl border-2 border-green-900"
        >
          {submitting ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Receiving…</span>
            </>
          ) : (
            <>
              <Save strokeWidth={3} className="h-6 w-6" />
              <span>Quick Receive + Add to Queue</span>
            </>
          )}
        </button>
        {!canSave && !submitting && (
          <div className="text-[11px] text-slate-600 mt-2 text-center" data-testid="quick-receive-hint">
            {!mrFilled
              ? "Type or scan the MR number above to enable."
              : "Pick a stocking spot above to enable."}
          </div>
        )}
      </div>
    </form>
  );
}

function FullReceiveForm({ form, setField, submitting, imageUploading, error, openScanner, onUpload, removeImage, onSubmit }) {
  const PRIORITY_OPTS = useDropdown("priority", PRIORITIES);
  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="full-receive-form">
      <Field label="MR Number *" testid="field-mr">
        <input
          required
          value={form.MR_Number}
          onChange={(e) => setField("MR_Number", e.target.value.toUpperCase())}
          data-testid="input-mr"
          className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-lg font-mono font-bold tracking-wider bg-white focus:border-slate-900 outline-none"
          placeholder="MR-123456"
        />
      </Field>

      <Field label="Project / Station *" testid="field-project-station">
        <SubstationCombobox
          value={form.Project_Station}
          onChange={(v) => setField("Project_Station", v)}
          testid="form-project-station"
          extraOptions={PROJECT_STATIONS}
          required
        />
      </Field>

      <Field label="Contents (short description)">
        <input
          value={form.Contents}
          onChange={(e) => setField("Contents", e.target.value)}
          data-testid="input-contents"
          className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
          placeholder="e.g. 6x 15kV switches, packing slip inside"
        />
      </Field>

      <Field label="Priority">
        <select
          value={form.Priority}
          onChange={(e) => setField("Priority", e.target.value)}
          data-testid="input-priority"
          className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none appearance-none"
        >
          {PRIORITY_OPTS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </Field>

      <Field label="Assigned Crew Leader (give to)">
        <CrewLeaderPicker
          value={form.Assigned_Crew_Leader}
          onChange={(v) => setField("Assigned_Crew_Leader", v)}
          testid="form-crew-leader-picker"
        />
      </Field>

      <Field label="Minor materials (lugs, bolts, washers, etc.)">
        <textarea
          value={form.Minor_Materials}
          onChange={(e) => setField("Minor_Materials", e.target.value)}
          data-testid="input-minor-materials"
          rows={3}
          className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
          placeholder="e.g. 24x bolts (1/2 in), 8 washers, 6 terminal blocks…"
        />
      </Field>

      <Field label="Equipment needed (Meggar, IR tester, etc.)">
        <textarea
          value={form.Equipment_Needed}
          onChange={(e) => setField("Equipment_Needed", e.target.value)}
          data-testid="input-equipment-needed"
          rows={2}
          className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
          placeholder="e.g. Meggar, Vacuum Bottle Tester, IR Tester"
        />
        <EquipmentStockPreview text={form.Equipment_Needed} />
      </Field>

      <Field label="Unavailable / missing items (highlighted in red)">
        <textarea
          value={form.Unavailable_Items}
          onChange={(e) => setField("Unavailable_Items", e.target.value)}
          data-testid="input-unavailable-items"
          rows={2}
          className="w-full border-2 border-rose-400 rounded-sm px-3 py-2 text-base bg-rose-50 focus:border-rose-700 outline-none"
          placeholder="e.g. 2x lugs missing — sub from spare bin"
        />
      </Field>

      <Field
        label="Stocking Location * (Where it will be stored in Main yard)"
        testid="field-full-storage"
      >
        <select
          value={form.Storage_Detail || ""}
          onChange={(e) => setField("Storage_Detail", e.target.value)}
          data-testid="full-input-storage"
          className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none min-h-[48px]"
        >
          <option value="">— Pick a stocking spot —</option>
          {STORAGE_DETAILS.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </Field>

      <Field label="Notes">
        <textarea
          value={form.Notes}
          onChange={(e) => setField("Notes", e.target.value)}
          data-testid="input-notes"
          rows={3}
          className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-base bg-white focus:border-slate-900 outline-none"
          placeholder="Damage, special handling, etc."
        />
      </Field>

      <Field label="Photos (boxes, packing slips, damage)">
        <div className="space-y-3">
          <div
            className="text-xs text-slate-600 flex items-start gap-1.5 leading-snug"
            data-testid="full-photo-recommended-hint"
          >
            <Camera className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
            <span>📸 Recommended: Take a photo of the handwritten MR number and packing slip for records.</span>
          </div>
          <label
            htmlFor="image-upload-input"
            className="rg-tap w-full bg-white border-2 border-dashed border-slate-400 hover:border-slate-900 rounded-sm flex items-center justify-center gap-2 font-bold uppercase tracking-wide text-slate-700 cursor-pointer min-h-[64px]"
            data-testid="image-upload-label"
          >
            <Upload strokeWidth={2.5} className="h-5 w-5" />
            {imageUploading ? "Uploading…" : "Add photos"}
          </label>
          <input
            id="image-upload-input"
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={onUpload}
            className="hidden"
            data-testid="image-upload-input"
          />
          {form.Images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="image-thumbs">
              {form.Images.map((p) => (
                <div key={p} className="relative aspect-square">
                  <AuthImg path={p} className="w-full h-full object-cover border-2 border-slate-300 rounded-sm" />
                  <button
                    type="button"
                    onClick={() => removeImage(p)}
                    data-testid={`remove-image-${p}`}
                    className="absolute -top-2 -right-2 h-7 w-7 bg-rose-700 text-white grid place-items-center rounded-sm border-2 border-white"
                  >
                    <X strokeWidth={3} className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Field>

      {error && (
        <div className="border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold px-3 py-2 rounded-sm" data-testid="form-error">
          {error}
        </div>
      )}

      <div className="md:hidden h-20" />
      <div className="fixed md:static bottom-16 md:bottom-auto left-0 right-0 md:left-auto md:right-auto z-30 bg-white md:bg-transparent border-t-2 md:border-t-0 border-slate-300 p-4 md:p-0">
        <button
          type="submit"
          disabled={submitting || imageUploading}
          data-testid="save-shipment-btn"
          className="rg-tap-xl w-full bg-blue-700 hover:bg-blue-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60 min-h-[64px] text-base sm:text-lg border-2 border-blue-900"
        >
          <Save strokeWidth={3} className="h-5 w-5" />
          {submitting ? "Saving…" : "Receive & Proceed to Stock"}
          <ArrowRight strokeWidth={3} className="h-5 w-5" />
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
